/**
 * Client-side auth: token in localStorage, refresh only when near expiry.
 *
 * Flow:
 * - Get token from localStorage (and in-memory cache).
 * - If token exists and is not near expiry → use it.
 * - If token missing or near expiry → call GET /api/v1/workflow-token
 *   (with ?refresh=1 only when we already have a token that's near expiry), store, use.
 * - authFetch() uses this and adds Authorization header; retries once on 401 with a fresh token.
 *
 * When to call refreshWorkflowToken() (force refresh to get latest group/team in token):
 * - After user creates a new org or team → refresh so token includes the new group/team.
 * - After user changes org/team selection (org-team-context) → refresh so token matches selection.
 * - After user accepts an invite (invite_accepted=1) → refresh so token includes new membership.
 *
 * Edge cases:
 * - Create org/team: we await refresh in onSuccess/onCreate before navigate so the new token
 *   is stored before the user lands on the next page.
 * - Refresh fails (e.g. network): next authFetch will retry with refresh on 401; user can refresh page.
 * - Multiple tabs: storage event syncs token; when one tab sets a new token, others see it.
 */

import { removeKey } from "./storage.client";
import { clearWebhookAttachReturn } from "./webhook-attach-return";

const AUTH_TOKEN_KEY = "authToken";

/** Same namespace and keys as `OrgTeamProvider.clearCache`. */
const ORG_TEAM_STORAGE_NAMESPACE = "org_team";

/** Refresh when token has less than this much time left (ms). */
const NEAR_EXPIRY_MS = 60_000;

const AUTH_TOKEN_CHANGED_EVENT = "authToken:changed";

let cachedAuthToken: string | null | undefined = undefined;

function safeGetLocalStorageItem(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSetLocalStorageItem(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // ignore (private mode / quota)
  }
}

function safeRemoveLocalStorageItem(key: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.removeItem(key);
  } catch {
    // ignore
  }
}

function dispatchTokenChanged(token: string | null): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(AUTH_TOKEN_CHANGED_EVENT, { detail: { token } }),
  );
}

function getJwtExpMs(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = parts[1]!;
    const b64 = payload.replaceAll("-", "+").replaceAll("_", "/");
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, "=");
    const json = atob(padded);
    const data = JSON.parse(json) as { exp?: number };
    if (typeof data.exp !== "number") return null;
    return data.exp * 1000;
  } catch {
    return null;
  }
}

/** Returns true if token exists and has at least `minMs` left until expiry. */
function isTokenValid(
  token: string | null,
  minMs: number = NEAR_EXPIRY_MS,
): boolean {
  if (!token) return false;
  const exp = getJwtExpMs(token);
  if (!exp) return false;
  return exp - Date.now() >= minMs;
}

/**
 * Get token from cache or localStorage (client-only).
 * Does not fetch or refresh.
 */
function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  if (cachedAuthToken !== undefined) return cachedAuthToken;
  const fromStorage = safeGetLocalStorageItem(AUTH_TOKEN_KEY);
  cachedAuthToken = fromStorage;
  return fromStorage;
}

/**
 * Set token in cache and localStorage; notify listeners.
 * Call this when you receive a token from the server or from a route loader.
 */
export function setAuthToken(
  token: string | null,
  _expiresAtMs?: number,
): void {
  cachedAuthToken = token;
  if (token) {
    safeSetLocalStorageItem(AUTH_TOKEN_KEY, token);
  } else {
    safeRemoveLocalStorageItem(AUTH_TOKEN_KEY);
  }
  dispatchTokenChanged(token);
}

/**
 * Clear workflow JWT and org/team client cache before server logout.
 * Server `logout()` is unchanged; avoids the next sign-in reusing another user's token or `x-active-org` headers.
 */
export function clearClientAuthStateOnLogout(): void {
  if (typeof window === "undefined") return;
  setAuthToken(null);
  removeKey("organizations", ORG_TEAM_STORAGE_NAMESPACE);
  removeKey("currentOrg", ORG_TEAM_STORAGE_NAMESPACE);
  removeKey("preferPersonalWorkspace", ORG_TEAM_STORAGE_NAMESPACE);
  removeKey("teams", ORG_TEAM_STORAGE_NAMESPACE);
  removeKey("currentTeam", ORG_TEAM_STORAGE_NAMESPACE);
  removeKey("orgImages", ORG_TEAM_STORAGE_NAMESPACE);
  // A parked webhook wizard holds the previous person's workflow inputs.
  clearWebhookAttachReturn();
}

/** Redirect to clear storage, destroy session, and login when token is invalid or refresh failed (no Keycloak logout page). */
function redirectToLoginDueToInvalidToken(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.clear();
  } catch {
    // ignore
  }
  setAuthToken(null);
  window.location.href = `/force-logout?destroy=1&redirectTo=${encodeURIComponent("/")}`;
}

/** Call GET /api/v1/workflow-token (optionally ?refresh=1), return token and optional expiresAt. */
async function fetchWorkflowToken(refresh: boolean): Promise<{
  token: string;
  expiresAtMs?: number;
} | null> {
  if (typeof window === "undefined") return null;
  const url = refresh
    ? "/api/v1/workflow-token?refresh=1"
    : "/api/v1/workflow-token";
  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "same-origin",
    });
    if (!response.ok) {
      if (response.status === 401) redirectToLoginDueToInvalidToken();
      return null;
    }
    const data = (await response.json()) as {
      token?: string;
      expiresAt?: string;
    };
    const token = data?.token ?? null;
    if (!token) return null;
    const expiresAtMs = data?.expiresAt
      ? new Date(data.expiresAt).getTime()
      : undefined;
    return { token, expiresAtMs };
  } catch {
    return null;
  }
}

let tokenFetchInFlight: Promise<string | null> | null = null;

/**
 * Get a valid token: from storage if still valid, otherwise fetch (and refresh only if near expiry).
 * - No token → GET /api/v1/workflow-token (no refresh), store, return.
 * - Token valid (not near expiry) → return it.
 * - Token near expiry or expired → GET /api/v1/workflow-token?refresh=1, store, return.
 */
export async function ensureAuthToken(options?: {
  refresh?: boolean;
  minValidityMs?: number;
}): Promise<string | null> {
  if (typeof window === "undefined") return null;

  const forceRefresh = Boolean(options?.refresh);
  const minValidityMs = options?.minValidityMs ?? NEAR_EXPIRY_MS;
  const current = getAuthToken();

  // Explicit refresh (e.g. after org/team change): always fetch with refresh=1.
  if (forceRefresh) {
    const p = (async () => {
      const thisFetch = tokenFetchInFlight;
      try {
        const result = await fetchWorkflowToken(true);
        if (result) {
          setAuthToken(result.token, result.expiresAtMs);
          return result.token;
        }
        return null;
      } finally {
        if (tokenFetchInFlight === thisFetch) tokenFetchInFlight = null;
      }
    })();
    tokenFetchInFlight = p;
    return p;
  }

  // Token exists and is valid for at least minValidityMs → use it.
  if (current && isTokenValid(current, minValidityMs)) return current;

  // No token or near/expired: fetch. Use refresh=1 only if we had a token (so server can refresh).
  const needRefresh = Boolean(current);
  if (tokenFetchInFlight) {
    const existing = await tokenFetchInFlight;
    if (existing && isTokenValid(existing, minValidityMs)) return existing;
  }
  const p = (async () => {
    const thisFetch = tokenFetchInFlight;
    try {
      const result = await fetchWorkflowToken(needRefresh);
      if (result) {
        setAuthToken(result.token, result.expiresAtMs);
        return result.token;
      }
      return null;
    } finally {
      if (tokenFetchInFlight === thisFetch) tokenFetchInFlight = null;
    }
  })();
  tokenFetchInFlight = p;
  return p;
}

/**
 * Force a token refresh (e.g. after org/team selection change).
 */
export async function refreshWorkflowToken(): Promise<string | null> {
  return ensureAuthToken({ refresh: true });
}

/**
 * fetch() wrapper: get valid token, add Authorization header, retry once on 401 with refresh.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit & {
    skipAuth?: boolean;
    refreshAuth?: boolean;
    minValidityMs?: number;
  },
): Promise<Response> {
  if (typeof window === "undefined") {
    return fetch(input, init);
  }

  const skipAuth = Boolean(init?.skipAuth);
  const minValidityMs = init?.minValidityMs ?? NEAR_EXPIRY_MS;
  const headers = new Headers(init?.headers);

  const applyToken = (token: string | null) => {
    if (skipAuth) return;
    if (token) headers.set("Authorization", `Bearer ${token}`);
    else headers.delete("Authorization");
  };

  const token = skipAuth
    ? null
    : await ensureAuthToken({
        refresh: init?.refreshAuth,
        minValidityMs,
      });
  applyToken(token);

  // Always send cookies for same-origin API calls so session auth works after client-side nav.
  const initWithCredentials = {
    ...init,
    credentials: init?.credentials ?? "same-origin",
  };

  // Bypass ngrok warning page
  headers.set("ngrok-skip-browser-warning", "true");

  let response = await fetch(input, { ...initWithCredentials, headers });

  if (!skipAuth && response.status === 401) {
    const refreshed = await ensureAuthToken({ refresh: true });
    if (refreshed && refreshed !== token) applyToken(refreshed);
    response = await fetch(input, { ...initWithCredentials, headers });
  }

  return response;
}

// Sync in-memory cache from storage (e.g. other tab updated).
if (typeof window !== "undefined") {
  cachedAuthToken = safeGetLocalStorageItem(AUTH_TOKEN_KEY);
  window.addEventListener("storage", (e) => {
    if (e.key === AUTH_TOKEN_KEY) {
      cachedAuthToken = e.newValue;
      dispatchTokenChanged(e.newValue);
    }
  });
}
